import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getNovaConversationId,
  selectChatMessages,
} from "utils/getNovaConversationDetails";
import {
  setNewConversation,
  setConversationLoading,
} from "store/AiFeatures/GoalsCreateWithNovaAI/action";
import useNovaAiResponseApi from "./useNovaAiResponseApi";
import useScreenWidth from "customHooks/useScreenWidth";
import useIsGoalsAiEnabled from "containers/Goals/pages/NewGoalsListingPage/hooks/useIsGoalsAiEnabled";

const useNovaChatBotWrapper = () => {
  const dispatch = useDispatch();
  const { propsForGoalsNovaAi } = useNovaAiResponseApi();
  const {
    createNewConversation,
    fetchConversationMessages,
    setChatPage,
    setHasMoreMessages,
  } = propsForGoalsNovaAi;

  const { isMobileView } = useScreenWidth();
  const { isGoalsAiEnabled } = useIsGoalsAiEnabled();
  const isNotFromOtherUser = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.notFromOtherUser
  );
  const isNovaGoalsAiEnabled = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.isNovaGoalsAiEnabled
  );
  const conversationId = useSelector(getNovaConversationId);
  const userNovaChatMessages = useSelector((state) =>
    selectChatMessages(state, conversationId)
  );
  const conversationLoadingStates = useSelector(
    (state) =>
      state?.aiFeatures?.goalsCreateWithNovaAI?.conversationLoadingStates
  );
  const isConversationMessagesLoading =
    conversationId &&
    conversationLoadingStates &&
    conversationLoadingStates[conversationId]?.loading;

  const accessibleKeys = useSelector(
    (s) => s?.ProductPermissions?.accessible_product_keys
  );
  const creditBalance = useSelector(
    (s) =>
      s?.aiFeatures?.credits?.creditBalance?.pbx_credits?.pbx_credit_balance ??
      0
  );
  const activeAiModels = useSelector(
    (s) => s?.aiFeatures?.aiConfigurations?.activeAiModels
  );

  const isGoalsAiEnabledFromState =
    Array.isArray(accessibleKeys) &&
    accessibleKeys.includes("peoplebox_ai") &&
    creditBalance > 0 &&
    Array.isArray(activeAiModels) &&
    activeAiModels.length > 0;

  const isAiEnabled = isGoalsAiEnabled || isGoalsAiEnabledFromState;

  const isNovaChatBotOpen = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.isNovaChatBotOpen
  );
  const isNewConversation = useSelector(
    (state) => state?.aiFeatures?.goalsCreateWithNovaAI?.isNewConversation
  );

  const [showUserMessage, setShowUserMessage] = useState(false);
  const [showNovaChatFromNovaIcon, setShowNovaChatFromNovaIcon] =
    useState(false);

  const showNovaChatBotIcon =
    !isMobileView &&
    isAiEnabled &&
    !isNovaChatBotOpen &&
    conversationId &&
    isNovaGoalsAiEnabled;

  useEffect(() => {
    if (isNewConversation) {
      createNewConversation();
      dispatch(setNewConversation(false));
    }
  }, [isNewConversation]);

  useEffect(() => {
    if (conversationId && isNovaChatBotOpen && isNotFromOtherUser) {
      dispatch(setConversationLoading(conversationId, true));
      fetchConversationMessages({ page: 1 });
      setChatPage(1);
      setHasMoreMessages(true);
    }
  }, [isNovaChatBotOpen, conversationId]);

  useEffect(() => {
    if (
      isNovaGoalsAiEnabled &&
      conversationId &&
      isNotFromOtherUser &&
      userNovaChatMessages.length === 0
    ) {
      dispatch(setConversationLoading(conversationId, true));
      fetchConversationMessages({ page: 1 });
      setChatPage(1);
      setHasMoreMessages(true);
    }
  }, [isNovaGoalsAiEnabled, conversationId, userNovaChatMessages.length]);

  return {
    propsForGoalsNovaAi,
    showUserMessage,
    setShowUserMessage,
    showNovaChatFromNovaIcon,
    setShowNovaChatFromNovaIcon,
    isAiEnabled,
    isNovaChatBotOpen,
    isMobileView,
    showNovaChatBotIcon,
    conversationId,
    isNewConversation,
    isNovaGoalsAiEnabled,
    isConversationMessagesLoading,
  };
};

export default useNovaChatBotWrapper;
